if (typeof VideoCarouselCard !== 'function') {

  class VideoCarousel extends HTMLElement {

    setScrollLock(locked) {
      if (locked) {
        this.setAttribute('data-lock-scroll', '');
      } else {
        this.removeAttribute('data-lock-scroll');
      }
    }

    pauseAllExcept(activeCard) {
      this.querySelectorAll('video-carousel-card').forEach((card) => {
        if (card !== activeCard) card.pausePlayback();
      });
    }

  }

  class VideoCarouselCard extends HTMLElement {

    constructor() {
      super();
      this._sourcesLoaded = false;
      this._observer = null;
      this._seeking = false;
      this._wasPlayingBeforeSeek = false;
      this._boundSeekMove = null;
      this._boundSeekEnd = null;
      this._onTimeUpdate = this._updateProgress.bind(this);
    }

    connectedCallback() {
      this.media = this.querySelector('[data-vc-media]');
      this.video = this.querySelector('video');
      this.playBtn = this.querySelector('[data-vc-play]');
      this.pauseOverlay = this.querySelector('[data-vc-pause-resume]');
      this.muteBtn = this.querySelector('[data-vc-mute]');
      this.muteIconMuted = this.querySelector('[data-vc-mute-icon-muted]');
      this.muteIconUnmuted = this.querySelector('[data-vc-mute-icon-unmuted]');
      this.controls = this.querySelector('[data-vc-controls]');
      this.progress = this.querySelector('[data-vc-progress]');
      this.progressTrack = this.querySelector('[data-vc-progress-track]');

      if (!this.video || !this.playBtn || !this.media) return;

      this.playBtn.addEventListener('click', this._onPlayClick.bind(this));

      if (this.pauseOverlay) {
        this.pauseOverlay.addEventListener('click', this._onPauseResumeClick.bind(this));
      }

      this.media.addEventListener('click', this._onMediaClick.bind(this));

      if (this.muteBtn) {
        this.muteBtn.addEventListener('click', this._onMuteClick.bind(this));
      }

      if (this.progressTrack) {
        this.progressTrack.addEventListener('pointerdown', this._onProgressPointerDown.bind(this));
      }

      this.video.addEventListener('timeupdate', this._onTimeUpdate);
      this.video.addEventListener('loadedmetadata', this._updateProgress.bind(this));

      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting && this.classList.contains('is-playing')) {
              this.pausePlayback();
            }
          });
        },
        { threshold: 0.35 }
      );
      this._observer.observe(this);
    }

    disconnectedCallback() {
      this._endSeek({ restorePlay: false });
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      if (this.video) {
        this.video.removeEventListener('timeupdate', this._onTimeUpdate);
      }
    }

    _carousel() {
      return this.closest('video-carousel');
    }

    _loadSources() {
      if (this._sourcesLoaded) return;
      this.video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
      });
      this.video.load();
      this._sourcesLoaded = true;
    }

    _isInteractiveTarget(target) {
      return Boolean(
        target &&
        target.closest('[data-vc-progress], [data-vc-mute], [data-vc-play], [data-vc-pause-resume]')
      );
    }

    _onPlayClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.startPlayback();
    }

    _onPauseResumeClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.resumePlayback();
    }

    _onMediaClick(event) {
      if (this._seeking || this._isInteractiveTarget(event.target)) return;
      if (!this.classList.contains('is-playing')) return;

      event.preventDefault();
      event.stopPropagation();

      if (this.video.paused || this.classList.contains('is-paused')) {
        this.resumePlayback();
      } else {
        this.softPause();
      }
    }

    _onMuteClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.video.muted = !this.video.muted;
      this._syncMuteUi();
    }

    startPlayback() {
      const carousel = this._carousel();
      if (carousel) carousel.pauseAllExcept(this);

      this._loadSources();
      this.classList.add('is-playing');
      this.classList.remove('is-paused');

      if (this.controls) this.controls.hidden = false;
      if (this.muteBtn) this.muteBtn.hidden = false;
      if (this.pauseOverlay) this.pauseOverlay.hidden = true;

      this.video.muted = true;
      this._syncMuteUi();

      const playPromise = this.video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          this.pausePlayback();
        });
      }
    }

    softPause() {
      if (!this.video) return;
      this.video.pause();
      this.classList.add('is-paused');
      if (this.pauseOverlay) this.pauseOverlay.hidden = false;
    }

    resumePlayback() {
      if (!this.video) return;
      this.classList.remove('is-paused');
      if (this.pauseOverlay) this.pauseOverlay.hidden = true;

      const playPromise = this.video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          this.softPause();
        });
      }
    }

    pausePlayback() {
      if (!this.video) return;
      this._endSeek({ restorePlay: false });
      this.video.pause();
      this.video.currentTime = 0;
      this.classList.remove('is-playing', 'is-paused', 'is-seeking');
      if (this.controls) this.controls.hidden = true;
      if (this.muteBtn) this.muteBtn.hidden = true;
      if (this.pauseOverlay) this.pauseOverlay.hidden = true;
      this.video.muted = true;
      this._syncMuteUi();
      this._updateProgress();
    }

    _syncMuteUi() {
      if (!this.muteBtn) return;
      const muted = this.video.muted;
      this.muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
      this.muteBtn.setAttribute(
        'aria-label',
        muted
          ? (this.dataset.labelUnmute || 'Unmute video')
          : (this.dataset.labelMute || 'Mute video')
      );
      if (this.muteIconMuted) this.muteIconMuted.hidden = !muted;
      if (this.muteIconUnmuted) this.muteIconUnmuted.hidden = muted;
    }

    _getDuration() {
      const duration = this.video.duration;
      return Number.isFinite(duration) && duration > 0 ? duration : 0;
    }

    _updateProgress() {
      if (!this.progressTrack || this._seeking) return;
      const duration = this._getDuration();
      const ratio = duration > 0 ? this.video.currentTime / duration : 0;
      const clamped = Math.min(1, Math.max(0, ratio));
      this.progressTrack.style.setProperty('--vc-progress', String(clamped));
      this.progressTrack.setAttribute('aria-valuenow', String(Math.round(clamped * 100)));
    }

    _seekRatioFromEvent(event) {
      const rect = this.progressTrack.getBoundingClientRect();
      if (!rect.width) return 0;
      const clientX = event.clientX != null ? event.clientX : (event.touches && event.touches[0] ? event.touches[0].clientX : 0);
      return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    }

    _applySeek(ratio) {
      const duration = this._getDuration();
      const clamped = Math.min(1, Math.max(0, ratio));
      this.progressTrack.style.setProperty('--vc-progress', String(clamped));
      this.progressTrack.setAttribute('aria-valuenow', String(Math.round(clamped * 100)));
      if (!duration) return;
      this.video.currentTime = clamped * duration;
    }

    _setScrollLock(locked) {
      const carousel = this._carousel();
      if (carousel) carousel.setScrollLock(locked);
      document.documentElement.classList.toggle('vc-scroll-lock', locked);
    }

    _onProgressPointerDown(event) {
      if (!this.classList.contains('is-playing')) return;

      event.preventDefault();
      event.stopPropagation();

      if (this.progressTrack.setPointerCapture && event.pointerId != null) {
        try {
          this.progressTrack.setPointerCapture(event.pointerId);
        } catch (err) {
          /* ignore */
        }
      }

      this._seeking = true;
      this._wasPlayingBeforeSeek = !this.video.paused && !this.classList.contains('is-paused');
      this.classList.add('is-seeking');
      this.classList.remove('is-paused');
      if (this.pauseOverlay) this.pauseOverlay.hidden = true;
      this.video.pause();
      this._setScrollLock(true);

      this._applySeek(this._seekRatioFromEvent(event));

      this._boundSeekMove = this._onProgressPointerMove.bind(this);
      this._boundSeekEnd = this._onProgressPointerEnd.bind(this);
      window.addEventListener('pointermove', this._boundSeekMove, { passive: false });
      window.addEventListener('pointerup', this._boundSeekEnd);
      window.addEventListener('pointercancel', this._boundSeekEnd);
    }

    _onProgressPointerMove(event) {
      if (!this._seeking) return;
      event.preventDefault();
      event.stopPropagation();
      this._applySeek(this._seekRatioFromEvent(event));
    }

    _onProgressPointerEnd(event) {
      if (!this._seeking) return;
      event.preventDefault();
      event.stopPropagation();

      if (this.progressTrack.releasePointerCapture && event.pointerId != null) {
        try {
          this.progressTrack.releasePointerCapture(event.pointerId);
        } catch (err) {
          /* ignore */
        }
      }

      this._endSeek({ restorePlay: this._wasPlayingBeforeSeek });
    }

    _endSeek({ restorePlay }) {
      if (this._boundSeekMove) {
        window.removeEventListener('pointermove', this._boundSeekMove);
        this._boundSeekMove = null;
      }
      if (this._boundSeekEnd) {
        window.removeEventListener('pointerup', this._boundSeekEnd);
        window.removeEventListener('pointercancel', this._boundSeekEnd);
        this._boundSeekEnd = null;
      }

      if (!this._seeking) return;

      this._seeking = false;
      this.classList.remove('is-seeking');
      this._setScrollLock(false);

      if (restorePlay && this.classList.contains('is-playing')) {
        this.resumePlayback();
      }
    }

  }

  if (typeof customElements.get('video-carousel') === 'undefined') {
    customElements.define('video-carousel', VideoCarousel);
  }

  if (typeof customElements.get('video-carousel-card') === 'undefined') {
    customElements.define('video-carousel-card', VideoCarouselCard);
  }

}

document.addEventListener('shopify:section:load', (event) => {
  if (!event.target.classList.contains('mount-video-carousel')) return;
  document.documentElement.classList.remove('vc-scroll-lock');
  event.target.querySelectorAll('video-carousel-card.is-playing').forEach((card) => {
    card.pausePlayback();
  });
});
